import { Turnstile } from '@marsidev/react-turnstile';
import { Box, Typography, Alert } from '@mui/material';
import { useCallback, useEffect } from 'react';

// Development mode bypass token
const DEV_BYPASS_TOKEN = 'dev-bypass-token';

export interface TurnstileWidgetProps {
  /** Callback when token is successfully obtained */
  onSuccess: (token: string) => void;
  /** Callback when token expires (should clear token state) */
  onExpire?: () => void;
  /** Callback when verification fails */
  onError?: (error: string) => void;
  /** Whether to show the widget (for conditional rendering) */
  show?: boolean;
  /** Additional helper text to display */
  helperText?: string;
}

/**
 * Reusable Cloudflare Turnstile widget component
 *
 * Wraps the @marsidev/react-turnstile component with:
 * - MUI styling
 * - Error handling
 * - Token expiration handling
 * - Mobile-first responsive design
 * - Auto-bypass in development when no site key is configured
 */
export function TurnstileWidget({
  onSuccess,
  onExpire,
  onError,
  show = true,
  helperText,
}: TurnstileWidgetProps) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const isDev = import.meta.env.DEV;
  const isDisabled = import.meta.env.VITE_TURNSTILE_DISABLED === 'true';

  const handleSuccess = useCallback(
    (token: string) => {
      onSuccess(token);
    },
    [onSuccess]
  );

  const handleExpire = useCallback(() => {
    onExpire?.();
  }, [onExpire]);

  const handleError = useCallback(
    (errorCode: string) => {
      const errorMessage = getErrorMessage(errorCode);
      onError?.(errorMessage);
    },
    [onError]
  );

  // Bypass condition: disabled via env var, or dev mode without site key
  const shouldBypass = isDisabled || (isDev && !siteKey);

  // Auto-pass when bypassed
  useEffect(() => {
    if (shouldBypass && show) {
      // Auto-call onSuccess with dev bypass token
      onSuccess(DEV_BYPASS_TOKEN);
    }
  }, [shouldBypass, show, onSuccess]);

  if (!show) {
    return null;
  }

  // Show bypass indicator when Turnstile is disabled
  if (shouldBypass) {
    return (
      <Box sx={{ my: 2 }}>
        <Alert severity="info">
          Turnstile bypassed in development mode
        </Alert>
      </Box>
    );
  }

  if (!siteKey) {
    return (
      <Box sx={{ my: 2 }}>
        <Alert severity="warning">
          Turnstile is not configured. Set VITE_TURNSTILE_SITE_KEY in environment.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        my: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {helperText && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, textAlign: 'center' }}
        >
          {helperText}
        </Typography>
      )}

      <Box
        sx={{
          minHeight: 65,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Turnstile
          siteKey={siteKey}
          onSuccess={handleSuccess}
          onExpire={handleExpire}
          onError={handleError}
          options={{
            theme: 'light',
            size: 'normal',
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * Get user-friendly error message from Turnstile error code
 */
function getErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'timeout-or-duplicate':
      return 'Verification timed out. Please try again.';
    case 'invalid-input-secret':
      return 'Configuration error. Please contact support.';
    case 'invalid-input-response':
      return 'Invalid verification response. Please try again.';
    case 'bad-request':
      return 'Verification request failed. Please try again.';
    case 'challenge-error':
      return 'Challenge failed to load. Please refresh the page.';
    default:
      return 'Verification failed. Please try again.';
  }
}

export default TurnstileWidget;
